import { CloseIcon, Dialog, Flex, ProviderConsumer as FluentUIThemeConsumer, Loader, Menu, Text, tabListBehavior } from '@fluentui/react-northstar';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { useHistory } from 'react-router-dom';
import rehypeRaw from 'rehype-raw'; // IMPORTANT: DO NOT USE WITHOUT DOM-PURIFY. This could lead to script injection.
import remarkGfm from 'remark-gfm';
import OfferingSvg from '../../../svg/service-offering.svg';
import { checkInTeams } from '../../App';
import { appState, useSetState } from '../../AppState';
import { Nav } from '../../browser-pages/browser-components/Nav';
import { getCachedFeature } from '../../shared/cache/FeatureCache';
import { RenderIcon } from '../../shared/components/EditIconProperty/EditIconProperty';
import { EmptyData } from '../../shared/components/EmptyData';
import { ResultCount } from '../../shared/components/ResultCount';
import { TablePagination } from '../../shared/components/table/TablePagination';
import { UpgradeNeeded } from '../../shared/components/UpgradeNeeded';
import { graphService } from '../../shared/services/graph.service';
import { platformService } from '../../shared/services/platform.service';
import { ConsentState, SignedInConsentState } from '../../shared/services/signInState.service';
import { CONTACT_ADMIN, FILTERS_STRINGS, LICENSE_FEATURE, SOMETHING_WENT_WRONG, TicketApprovalState, UNAUTHORIZED_ACCESS, markdownComponents } from '../../shared/utils/constants';
import { loadEndUserCFOptions } from '../ticketHelper';
import FlashlightSVG from './../../../svg/flashlight.svg';
import { NewRequest } from './NewRequest';
import RequestContextProvider, { RequestListContext } from './RequestList/RequestContextProvider';
import RequestFunctions from './RequestList/RequestFunctions';
import { RequestToolbar } from './RequestToolbar';
import { RequestPageSkeleton } from './Skeletons/RequestPageSkeleton';
import { CatalogPageSkeleton } from './Skeletons/ServiceCatalogSkeleton';
import { ApprovalColumn } from './TicketRequestCard/ApprovalColumn';
import { TicketRequestCard } from './TicketRequestCard/TicketRequestCard';
import { CatalogImage } from '../../shared/components/Wysiwyg/CatalogImage';
import { RefreshBar } from '../../shared/components/table/RefreshBar';



export enum RequestColumns {
  None = 0,
  Requests = 1 << 0,
  Approvals = 1 << 1,
  ServiceCatalog = 1 << 2,
  RequestsAndApprovals = 0b11,
  All = ~(~0 << 3) // This should change to the number of Enums you have, so you can check if all are selected
};

export const Requests = () => {
  const { t } = useTranslation();

  return (<>
    <Helmet>
      <title>{t('ticket.toolbar.tickets')} - Tikit</title>
    </Helmet>
    <RequestContextProvider>
      <RequestsPage />
    </RequestContextProvider>
  </>);
};

export const ReloadEndUserCFOptions = async (api, graphAPI, currentState, setState, awaitTeamMembers = false) => {

  let FAILEDAPICOUNT = 0;

  const loadFiltersData = async () => {
    try {
      const newState = await loadEndUserCFOptions(api, graphAPI, currentState, setState, awaitTeamMembers);
      FAILEDAPICOUNT = 0;
      return newState;
    } catch (error) {
      if (++FAILEDAPICOUNT <= 5) loadFiltersData();
      console.error(error);
    }
  };

  return await loadFiltersData();
}

const filterCatalog = (serviceCatalog: any[], search: string): any[] => {
  if ((search?.trim().length ?? 0) === 0) return serviceCatalog;

  const filteredCatalog = serviceCatalog.map(s => {
    const o = { ...s };
    if ((s.name ?? "")?.toLowerCase().includes(search.toLowerCase()) || (s.description ?? "")?.toLowerCase().includes(search.toLowerCase()))
      return o;

    o.offerings = o.offerings.filter(t => (t.requestOffering ?? "")?.toLowerCase().includes(search.toLowerCase()) ||
      (t.requestDescription ?? "")?.toLowerCase().includes(search.toLowerCase()));

    o.articles = o.articles.filter(t => (t.requestOffering ?? "")?.toLowerCase().includes(search.toLowerCase()) ||
      (t.requestDescription ?? "")?.toLowerCase().includes(search.toLowerCase()));

    return (o.offerings.length > 0 || o.articles.length > 0) ? o : null;
  }).filter(s => s !== null);

  return filteredCatalog;
};

const RequestsPage = () => {
  const { t } = useTranslation();
  const api = new platformService();
  const setState = useSetState();
  const currentState = appState();
  const history = useHistory();

  const graphAPI = new graphService();

  const { getPaginatedRequests, moveToRequestPage, countPerRequestPage, getPaginatedApprovals, moveToApprovalPage, countPerApprovalPage } = RequestFunctions();
  const { requestStates, setRequestStates, approvalStates, setApprovalStates, showClosed, setShowClosed, searchFilter, setSearchFilter } = useContext(RequestListContext);

  const [catalogLoading, setCatalogLoading] = useState(true);
  const [initialCatalogLoad, setInitialCatalogLoad] = useState(true);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [catalogExpanded, setCatalogExpanded] = useState({});
  // While searching, every matching section opens so its matches show, whatever its collapsed-by-default setting;
  // a section closed by hand during the search stays closed until the search changes. Clearing it puts each back.
  const [closedWhileSearching, setClosedWhileSearching] = useState<Record<string, boolean>>({});
  const isSearching = !!searchFilter?.trim();
  useEffect(() => setClosedWhileSearching({}), [searchFilter]);
  const isSectionOpen = (id: string | number) => (isSearching ? !closedWhileSearching[id] : (catalogExpanded[id] || false));

  const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);
  const [catalogEnabled, setCatalogEnabled] = useState<boolean | null>(null);
  const [items, setItems] = useState([
    {
      key: 'tickets',
      content: 'Tickets',
    },
    {
      key: 'approvals',
      content: 'Approvals',
    }
  ]);

  const [isSignedIn] = SignedInConsentState.useIsSignedIn();
  const isReloadClickedRef = useRef(false);

  const [dialog, setDialog] = useState(false);
  const [dialogClose, setDialogClose] = useState(true);
  const [column, setColumn] = useState<RequestColumns>(RequestColumns.All);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const setError = (e: any) => {
    const errCode = e.response ? e.response.status : 500;
    let errorstate = {
      ...currentState,
      isLoading: false,
      isError: true,
      errorConfig: { fields: { title: errCode == 401 || errCode == 403 ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG, desc: CONTACT_ADMIN } }
    };
    if (!_.isEqual(currentState, errorstate)) {
      setState(errorstate);
    }
  };

  const checkFeatures = async () => {
    const results = await Promise.all([
      getCachedFeature(LICENSE_FEATURE.TicketLifecycle),
      getCachedFeature(LICENSE_FEATURE.ServiceCatalog),
      getCachedFeature(LICENSE_FEATURE.TicketPortal),
      (currentState?.teamsConfig?.TenantId) ? currentState?.teamsConfig : (await api.getTeamsBotConfiguration()).data
    ]);
    setIsFeatureEnabled(results[2]);
    const catalog = results[1] && results[3].EnableServiceCatalog;
    setCatalogEnabled(catalog);
    
    if (catalog)
      setItems(p => [{ key: 'catalog', content: 'Service Catalog' }, ...p]);

    return [catalog, results[3]];
  };
  const loadPage = async (catalogEnabled: boolean, pageConfig: any) => {
    try {
      const results = await Promise.all([
        ReloadEndUserCFOptions(api, graphAPI, currentState, setState),
        (currentState?.defaultTeam?.Id) ? [currentState?.defaultTeam] : api.getTeamsSafe(FILTERS_STRINGS.IS_DEFAULT)
      ]);

      const defaultTeam = results[1][0];

      setState(prevState => {
        prevState.defaultTeam = defaultTeam
        prevState.teamsConfig = pageConfig
        return prevState;
      });
    } catch (err) {
      console.error(err);
      setError(err);
    }

    setIsDataLoading(false);
  };
  const loadCatalog = async (enabled?: boolean) => {
    enabled ??= catalogEnabled;
    if (!enabled) {
      setCatalogLoading(false);
      setInitialCatalogLoad(false);
      setServiceCatalog([]);
      return;
    }

    setCatalogLoading(true);
    try {
      const data = await api.getServiceCatalog();
      setServiceCatalog(data.data.value);
      setCatalogExpanded(p => {
        const expand = {};
        data.data.value.forEach(x => { expand[x.id] = x.expanded; });
        return expand;
      });
    } catch (err) {
      console.error(err);
      setServiceCatalog([]);
    }

    setCatalogLoading(false);
    setInitialCatalogLoad(false);
  }

  const loadItems = async () => {
    const [catalogEnabled, pageConfig] = await checkFeatures();
    await loadPage(catalogEnabled, pageConfig);

    getPaginatedRequests(requestStates.currentPage);
    getPaginatedApprovals(approvalStates.currentPage);
    loadCatalog(catalogEnabled);
  };

  const refreshLists = (checked: boolean, search: string, filtering: boolean) => {
    setRequestStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, currentPage: 1 }));
    setApprovalStates((prev) => ({ ...prev, isApprovalsFetching: true, currentPage: 1 }));
    getPaginatedRequests(1, requestStates.countPerPage, checked, search);
    getPaginatedApprovals(1, approvalStates.countPerPage, checked, search);
    if (!filtering) loadCatalog();
  };

  const toggleClosed = (checked: boolean) => {
    setShowClosed(checked);
    refreshLists(checked, undefined, true);
  };

  // Searches once typing pauses, as the agent ticket list does (400ms, was 2s); Enter searches at once (flush).
  const searchItems = useCallback(_.debounce((searchText: string) => {
    setSearchFilter(searchText);
    refreshLists(undefined, searchText, true);
  }, 400), [showClosed, requestStates, approvalStates]);

  const refreshPage = useCallback(() => {
    setState(state => ({ ...state, isReload: true }));
    setRequestStates(state => ({ ...state, isTicketsFetching: true, errorDuringFetch: false }));
    setApprovalStates(state => ({ ...state, isApprovalsFetching: true }));
    refreshLists(undefined, undefined, false);
  }, [currentState, requestStates, approvalStates, showClosed, searchFilter]);

  const onApprovalUpdate = (approvalId: number, approverId: number, status: TicketApprovalState, approvalState: TicketApprovalState) => { };

  useEffect(() => {
    if (isSignedIn == ConsentState.SignedIn && !isReloadClickedRef.current && !currentState.isReload)
      loadItems();
    if (isSignedIn == ConsentState.SignedIn && currentState.isReload)
      loadItems();

    if (!currentState.isReload)
      isReloadClickedRef.current = false;
  }, [isSignedIn, currentState.isReload]);

  useEffect(() => {
    if (approvalStates.isInitialLoad || initialCatalogLoad) return;

    let newColumn = column;
    if (!approvalStates.isApprovalsFetching)
      newColumn = approvalStates.countApprovals === 0 ? (newColumn & ~RequestColumns.Approvals) : (newColumn | RequestColumns.Approvals);

    if (catalogEnabled && catalogLoading)
      newColumn = newColumn | RequestColumns.ServiceCatalog;
    else if (!catalogLoading || !catalogEnabled)
      newColumn = serviceCatalog.length === 0 ? (newColumn & ~RequestColumns.ServiceCatalog) : (newColumn | RequestColumns.ServiceCatalog);

    if (newColumn !== column)
      setColumn(newColumn);
  }, [approvalStates.isInitialLoad, initialCatalogLoad]);

  const getWidth = (type: RequestColumns, col: RequestColumns) => {
    if (col === type) return 'md:w-1/2';

    let factor = col & RequestColumns.ServiceCatalog ? 2 : 1;

    factor *= (type === RequestColumns.Requests && (col & RequestColumns.Approvals) === RequestColumns.Approvals) ||
      (type === RequestColumns.Approvals && (col & RequestColumns.Requests) === RequestColumns.Requests) ? 2 : 1;

    return factor === 1 ? '' : `md:w-1/${factor}`;
  };

  const renderEmptyData = () => {
    return <EmptyData SVGIcon={<FlashlightSVG width={200} height={200} className={'pb-2'} />} headerText={t('requests.no-matches')} subheaderText={t('common.try-a-different-search')} style={{ height: 'calc(100vh - 165px - 3.25rem)' }} />
  }

  return ((!isFeatureEnabled) ? <UpgradeNeeded toolbarText={t('ticket.toolbar.tickets')} headerText={t('ticket-portal.no-access-current-plan')} subheaderText={t('ticket-portal.no-access-upgrade-plan')} /> :
    <FluentUIThemeConsumer render={(globalTheme) => {
      const scItems = filterCatalog(serviceCatalog, searchFilter);

      return (<>
        {!checkInTeams() && <Nav />}
        <RequestToolbar
          globalTheme={globalTheme}
          initialFilterValue={showClosed}
          changeFilter={toggleClosed}
          searchTickets={searchItems}
          onClickRefresh={refreshPage}
          onClickRequest={() => { setDialog(true); }}
          onChangeColumn={(type, checked) => { setColumn(p => checked ? (p | type) : (p & ~type)) }}
          column={column}
          hasApprovals={approvalStates.approvals.length > 0}
          showRequest={(currentState?.teamsConfig?.RequestBehavior ?? "Hidden") !== "Hidden"}
          catalogEnabled={catalogEnabled}
        />
        <div className={`h-screen`}>

          <div className='block md:hidden'>
            <Menu
              defaultActiveIndex={0}
              items={items}
              underlined
              primary
              accessibility={tabListBehavior}
              aria-label="Today's events"
              onActiveIndexChange={(_e, p) => {
                switch (p.activeIndex) {
                  case 1:
                    setColumn(catalogEnabled ? RequestColumns.Requests : RequestColumns.Approvals);
                    break;
                  case 2:
                    setColumn(RequestColumns.Approvals);
                    break;
                  default:
                    setColumn(catalogEnabled ? RequestColumns.ServiceCatalog : RequestColumns.Requests);
                    break;
                }
              }}
            />
          </div>
          <div style={{ maxWidth: '1920px', margin: 'auto' }} className={`h-full px-5 md:p-0`}>
            <div className={`flex w-full justify-center`}>
              {catalogEnabled && <div className={`w-full md:w-1/2 px-0 py-5 md:px-5 ${column & RequestColumns.ServiceCatalog ? '' : 'hidden-important'}`}>
                <div className='md:block hidden'>
                  <Text content={t('requests.service-catalog')} weight={'semibold'} size='large' />
                </div>
                <div className={`overflow-auto request-column-catalog md:request-column-catalog${catalogLoading && serviceCatalog.length > 0 ? ' cards-refreshing' : ''}`}>
                  {catalogLoading && serviceCatalog.length > 0 && <RefreshBar />}
                  {catalogLoading && serviceCatalog.length === 0 && <CatalogPageSkeleton />}
                  {!catalogLoading && scItems.length === 0 && renderEmptyData()}
                  {(!catalogLoading || serviceCatalog.length > 0) && scItems.map(s => (
                    <>
                      <div className={`px-4 pt-4 pb-2 mt-3 rounded-md cursor-pointer`} style={{ backgroundColor: isSectionOpen(s.id) ? globalTheme.siteVariables.colorScheme.default.backgroundActive : globalTheme.siteVariables.colorScheme.default.background, boxShadow: '0 4px 3px rgb(0 0 0 / 0.07)' }}
                        onClick={() => {
                          if (isSearching) setClosedWhileSearching(p => ({ ...p, [s.id]: !p[s.id] }));
                          else setCatalogExpanded(p => ({ ...p, [s.id]: !(p[s.id] || false) }));
                        }}>
                        <Flex gap="gap.small">
                          <RenderIcon icon={s.icon} defaultIcon={<OfferingSvg fill="#6264A7" width={40} height={40} />} />
                          <Flex column>
                            <Text content={s.name} weight='semibold' size='larger' />
                            <Text content={s.description} />
                          </Flex>
                        </Flex>
                        <div className={`catalog-items px-4 pt-2 pb-2 mt-3 rounded-md cursor-default ${isSectionOpen(s.id) ? '' : 'hidden'}`} style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background, boxShadow: '0 4px 3px rgb(0 0 0 / 0.07)' }}>
                          {s.offerings.length > 0 && (<>
                            <Flex className='space-x-1 items-center'>
                              <span className='service-catalog request-offering'>&nbsp;</span>
                              <Text content={t('requests.catalog.offerings')} weight='semibold' size='large' />
                            </Flex>
                            <Flex className='py-2 gap-2 flex-wrap'>
                              {s.offerings.map(o => {
                                const match = /^ApplyTemplate:(?<id>\d+)$/i.exec(o.answer);
                                let templateId = 0;
                                if (match)
                                  templateId = parseInt(match.groups['id'], 10);

                                return <Flex column gap="gap.small" onClick={(e) => { history.push(`/requestcatalog/${o.id}/template/${templateId}`); e.stopPropagation(); }} className={`px-4 pt-2 pb-2 rounded-md cursor-pointer flex-basis-full md:flex-basis-half`} style={{ borderColor: globalTheme.siteVariables.colorScheme.default.backgroundActive, borderStyle: 'solid', borderWidth: '1px' }}>
                                  <Text content={o.requestOffering} weight='semibold' />
                                  <Text size='small'><Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={{ ...markdownComponents, img: CatalogImage }}>{DOMPurify.sanitize(o.requestDescription)}</Markdown></Text>
                                </Flex>;
                              })}
                              {s.offerings.length % 2 > 0 && (<Flex.Item className="hidden md:block flex-basis-full md:flex-basis-half"><div>&nbsp;</div></Flex.Item>)}
                            </Flex>
                          </>)}
                          {s.articles.length > 0 && (<>
                            <Flex className='space-x-1 items-center'>
                              <span className='service-catalog knowledge-article'>&nbsp;</span>
                              <Text content={t('requests.catalog.articles')} weight='semibold' size='large' />
                            </Flex>
                            <Flex className='py-2 gap-2 flex-wrap'>
                              {s.articles.map(o => (<Flex column gap="gap.small" onClick={(e) => { history.push(`/requestcatalog/${o.id}/article/${o.id}`); e.stopPropagation(); }} className={`px-4 pt-2 pb-2 rounded-md cursor-pointer flex-basis-full md:flex-basis-half`} style={{ borderColor: globalTheme.siteVariables.colorScheme.default.backgroundActive, borderStyle: 'solid', borderWidth: '1px' }}>
                                <Text content={o.requestOffering} weight='semibold' />
                                <Text size='small'><Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={{ ...markdownComponents, img: CatalogImage }}>{DOMPurify.sanitize(o.requestDescription)}</Markdown></Text>
                              </Flex>))}
                              {s.articles.length % 2 > 0 && (<Flex.Item className="hidden md:block flex-basis-full md:flex-basis-half"><div>&nbsp;</div></Flex.Item>)}
                            </Flex>
                          </>)}
                        </div>
                      </div>
                    </>
                  ))}
                  <div className='mobile-tab-padding md:hidden'>&nbsp;</div>
                </div>
              </div>}
              <div className={`w-full ${getWidth(RequestColumns.Requests, column)} ${(catalogEnabled && column & RequestColumns.ServiceCatalog) ? 'hidden md:block' : ''} px-0 py-5 md:px-5 ${column & RequestColumns.Requests ? '' : 'hidden-important'}`}>
                <div className='md:block hidden'>
                  <Text content={t('requests.tickets')} weight={'semibold'} size='large' />
                </div>
                {/* Refresh, search, Show Closed and paging keep the cards on screen, dimmed under the loading bar, as the
                    agent lists do (Shaun, 2026-10-20); the shimmer is only for a list with nothing to show yet. */}
                {requestStates.isTicketsFetching && requestStates.tickets.length === 0 && (<RequestPageSkeleton />)}
                {!requestStates.isTicketsFetching && requestStates.tickets.length === 0 && (renderEmptyData())}
                {requestStates.tickets.length > 0 && (
                  <div className={`overflow-auto request-column-ticket md:request-column-ticket${requestStates.isTicketsFetching ? ' cards-refreshing' : ''}`}>
                    {requestStates.isTicketsFetching && <RefreshBar />}
                    {/* Keyed by ticket: by position, a search or Show Closed handed each card another ticket and its avatar
                        showed the old one's photo until the new one loaded - a flicker as the results came in. */}
                    {requestStates.tickets.map((ticket: Ticket) => (<TicketRequestCard key={ticket.Id} ticket={ticket} view='list' userId={currentState.currentUserId} />))}
                  </div>
                )}
                {requestStates.tickets.length > 0 && (
                  <div className={`flex items-center${requestStates.isTicketsFetching ? ' cards-refreshing' : ''}`}>
                    <ResultCount count={requestStates.countTickets} />
                    <TablePagination currentPage={requestStates.currentPage} totalCount={requestStates.countTickets} moveToPage={moveToRequestPage} itemsPerPage={requestStates.countPerPage} itemsPerPageChanged={countPerRequestPage} />
                  </div>
                )}
              </div>
              <div className={`w-full ${getWidth(RequestColumns.Approvals, column)} ${((catalogEnabled && column & RequestColumns.ServiceCatalog) || (column & RequestColumns.Requests)) ? 'hidden md:block' : ''} px-0 py-5 md:px-5 ${column & RequestColumns.Approvals ? '' : 'hidden-important'}`}>
                <div className='md:block hidden'>
                  <Text content={t('requests.approvals-columns.approvals')} weight={'semibold'} size='large' />
                </div>
                {approvalStates.isApprovalsFetching && approvalStates.approvals.length === 0 && (<RequestPageSkeleton />)}
                {!approvalStates.isApprovalsFetching && approvalStates.approvals.length === 0 && (renderEmptyData())}
                {approvalStates.approvals.length > 0 && (
                  <ApprovalColumn
                    pagination={{
                      currentPage: approvalStates.currentPage,
                      totalCount: approvalStates.countApprovals,
                      moveToPage: moveToApprovalPage,
                      itemPerPage: approvalStates.countPerPage,
                      itemsPerPageChanged: countPerApprovalPage
                    }}
                    paginatedApprovals={approvalStates.approvals}
                    userId={currentState.currentUserId}
                    column={column}
                    onUpdate={onApprovalUpdate}
                    refreshing={approvalStates.isApprovalsFetching}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <Dialog
          open={dialog}
          trapFocus={{ firstFocusableSelector: '.new-request-dialog textarea' }}
          content={{
            content: (isDataLoading ? <><Loader className='m-4' label={t('common.loading')} /></> : <NewRequest globalTheme={globalTheme} onCreateTicket={() => setDialogClose(false)} onCreateComplete={() => setDialogClose(true)} teamsConfig={currentState.teamsConfig} onCancel={() => setDialog(false)}/>),
            styles: { marginBottom: 0 },
            className: `new-request-dialog`,
            id: 'new-request'
          }}
          header={isDataLoading ? "" : t('requests.new-request')}
          headerAction={(dialogClose ? {
            icon: <CloseIcon />,
            title: 'Close',
            onClick: () => setDialog(false)
          } : <></>)}
          className='w-full-important md:max-w-flyout max-h-screen overflow-y-auto'
        />
      </>
      )
    }} />
  );
};

